import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Sparkles, Shield, Clock } from 'lucide-react';
import { aboutContent, galleryImages } from '../data/mock';
import { useSettings } from '../context/SettingsContext';

const About = () => {
  const { businessInfo } = useSettings();
  
  return (
    <main>
      {/* Hero */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-widest text-[var(--text-secondary)] mb-2">About</p>
            <h1 className="hero-medium mb-4">The HomeGlow Story</h1>
            <p className="body-large text-[var(--text-secondary)]">
              Discover the passion and dedication behind HomeGlow Nails.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src={galleryImages[3].url}
                alt="About HomeGlow Nails"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <div>
              <h2 className="heading-1 mb-6">Welcome to HomeGlow Nails</h2>
              <p className="body-large text-[var(--text-secondary)] mb-6">
                {aboutContent.story}
              </p>
              <p className="body-regular text-[var(--text-secondary)] mb-6">
                {aboutContent.mission}
              </p>
              <p className="body-regular text-[var(--text-secondary)] mb-8">
                {aboutContent.experience}
              </p>
              <Link to="/book" className="btn-primary">
                Book With Me
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="heading-1 mb-4">Why Choose HomeGlow?</h2>
            <p className="body-regular text-[var(--text-secondary)]">
              Experience the difference of a dedicated, private nail studio.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.whyChoose.map((item, index) => {
              const icons = [Heart, Sparkles, Shield, Clock];
              const Icon = icons[index];
              return (
                <div key={index} className="text-center p-8 bg-[var(--bg-primary)]">
                  <div className="inline-flex items-center justify-center w-12 h-12 mb-4">
                    <Icon size={24} strokeWidth={1.5} className="text-[var(--text-primary)]" />
                  </div>
                  <h3 className="heading-3 mb-2">{item.title}</h3>
                  <p className="body-small text-[var(--text-secondary)]">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Studio Features */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="heading-1 mb-6">A Studio Designed for Comfort</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-1 bg-[var(--accent-blush)] flex-shrink-0" />
                  <div>
                    <h3 className="heading-3 mb-2">Private Setting</h3>
                    <p className="body-small text-[var(--text-secondary)]">
                      No noisy salons or rushing between clients. Just you and me in a calm, 
                      dedicated space where you can truly relax.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1 bg-[var(--accent-nude)] flex-shrink-0" />
                  <div>
                    <h3 className="heading-3 mb-2">Premium Products</h3>
                    <p className="body-small text-[var(--text-secondary)]">
                      I use only high-quality, professional-grade polishes and products 
                      that are healthier for your nails and longer-lasting.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1 bg-[var(--accent-gold)] flex-shrink-0" />
                  <div>
                    <h3 className="heading-3 mb-2">Strict Hygiene</h3>
                    <p className="body-small text-[var(--text-secondary)]">
                      Your safety is my priority. All tools are properly sterilized, 
                      and single-use items are always new for each client.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src={galleryImages[7].url}
                alt="HomeGlow Studio"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-padding-small bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="heading-2 mb-4">Serving the Tri-City Area</h2>
            <p className="body-regular text-[var(--text-secondary)] mb-6">
              Conveniently located in Kitchener, Ontario. Easy access for clients from 
              Waterloo, Cambridge, and surrounding areas.
            </p>
            <p className="body-small text-[var(--text-light)]">
              {businessInfo.address}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="hero-medium mb-4">Ready to Meet?</h2>
            <p className="body-large text-[var(--text-secondary)] mb-8">
              Book your first appointment and experience the HomeGlow difference.
            </p>
            <Link to="/book" className="btn-primary">
              Book Appointment
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
